
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { persistAttributionToProfile } from "@/lib/attribution";
import { useTranslation } from "react-i18next";

import i18n from "@/i18n";
const AuthCallback = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        console.log("=== OAuth Callback Handler Started ===");
        console.log("Current URL:", window.location.href);
        console.log("URL Hash:", window.location.hash);
        console.log("URL Search:", window.location.search);
        console.log("URL Search Params:", new URLSearchParams(window.location.search));
        
        // Parse URL parameters to see what Google sent back
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        
        console.log("URL Params entries:", Array.from(urlParams.entries()));
        console.log("Hash Params entries:", Array.from(hashParams.entries()));
        
        // Check for error parameters
        const error = urlParams.get('error') || hashParams.get('error');
        const errorDescription = urlParams.get('error_description') || hashParams.get('error_description');
        
        if (error) {
          console.error("OAuth error from URL:", { error, errorDescription });
          toast.error(i18n.t("toastsUi.auth.oauthError", { message: errorDescription || error }));
          navigate("/sign-in");
          return;
        }
        
        // Check for authorization code or access token
        const code = urlParams.get('code');
        const accessToken = hashParams.get('access_token');
        const sessionData = hashParams.get('session');
        
        console.log("OAuth response data:", { 
          code: code ? "present" : "missing",
          accessToken: accessToken ? "present" : "missing",
          sessionData: sessionData ? "present" : "missing"
        });
        
        // Wait a moment for Supabase to process the callback
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Get the session from Supabase
        console.log("Attempting to get session from Supabase...");
        const { data, error: sessionError } = await supabase.auth.getSession();
        console.log("getSession result:", { 
          hasSession: !!data.session,
          sessionError,
          userId: data.session?.user?.id,
          email: data.session?.user?.email,
          provider: data.session?.user?.app_metadata?.provider
        });
        
        if (sessionError) {
          console.error("Session retrieval error:", sessionError);
          toast.error(i18n.t("toastsUi.auth.authFailed", { message: sessionError.message }));
          navigate("/sign-in");
          return;
        }

        if (data.session) {
          console.log("✅ OAuth callback successful!", {
            userId: data.session.user.id,
            email: data.session.user.email,
            provider: data.session.user.app_metadata?.provider
          });

          // Check if this is a new user by checking profile creation
          const { data: profile } = await supabase
            .from('profiles')
            .select('welcome_email_sent')
            .eq('id', data.session.user.id)
            .single();

          // Send welcome email if not already sent
          if (profile && !profile.welcome_email_sent) {
            try {
              const { error: emailError } = await supabase.functions.invoke('send-welcome-email', {
                body: {
                  user_email: data.session.user.email,
                  user_name: data.session.user.user_metadata?.name || data.session.user.email?.split('@')[0],
                  language: i18n.language,
                }
              });

              if (!emailError) {
                // Mark welcome email as sent
                await supabase
                  .from('profiles')
                  .update({
                    welcome_email_sent: true,
                    welcome_email_sent_at: new Date().toISOString()
                  })
                  .eq('id', data.session.user.id);

                console.log("Welcome email sent successfully");
              } else {
                console.error("Failed to send welcome email:", emailError);
              }
            } catch (error) {
              // Don't block user flow if email fails
              console.error("Error sending welcome email:", error);
            }

            // Generate unique welcome discount promo code
            try {
              await supabase.functions.invoke('generate-welcome-discount', {
                body: { user_id: data.session.user.id }
              });
              console.log("Welcome discount generated successfully");
            } catch (error) {
              console.error("Error generating welcome discount:", error);
            }

            // Persist captured UTM/referrer to profile (non-blocking)
            persistAttributionToProfile(data.session.user.id);
          }

          toast.success(t("auth.callback.successGoogle"));
          navigate("/dashboard");
        } else {
          console.log("❌ No session found in callback");
          
          // Additional debugging: Check auth state directly
          console.log("Checking auth state directly...");
          const { data: user } = await supabase.auth.getUser();
          console.log("Direct user check:", { 
            hasUser: !!user.user,
            userId: user.user?.id 
          });
          
          toast.error(t("auth.callback.sessionNotEstablished"));
          navigate("/sign-in");
        }
      } catch (error: any) {
        console.error("❌ OAuth callback catch block:", error);
        console.error("Error details:", {
          message: error.message,
          stack: error.stack,
          name: error.name
        });
        toast.error(t("auth.callback.unexpectedFailure"));
        navigate("/sign-in");
      }
    };

    // Add a small delay to ensure the URL is fully loaded
    const timer = setTimeout(handleAuthCallback, 100);

    return () => clearTimeout(timer);
  }, [navigate, t]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">{t("auth.callback.completing")}</p>
        <p className="text-sm text-muted-foreground mt-2">{t("auth.callback.pleaseWait")}</p>
      </div>
    </div>
  );
};

export default AuthCallback;
